import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Icon,
  Link as ChakraLink,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiGlobe } from 'react-icons/fi';
import { useResearcherProfile } from '../../hooks/useResearchers';
import { NewMessageButton } from '../messages/NewMessageButton';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { RESEARCH_TYPES } from '../../utils/formatters';

export default function ResearcherDetail() {
  const { researcherId } = useParams();
  const navigate = useNavigate();
  const { profile, loading } = useResearcherProfile(researcherId);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  if (loading) return <LoadingSpinner message="Loading profile..." />;

  if (!profile) {
    return (
      <Container maxW="3xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>Researcher not found</Heading>
        <Button onClick={() => navigate('/researchers')} leftIcon={<Icon as={FiArrowLeft} />}>
          Back to Researchers
        </Button>
      </Container>
    );
  }

  const rep = profile.reputation;

  return (
    <Container maxW="3xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Button
          onClick={() => navigate('/researchers')}
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Researchers
        </Button>

        {/* Profile header */}
        <Box bg={cardBg} rounded="xl" p={8} borderWidth={1} borderColor={borderColor}>
          <VStack spacing={6} align="center">
            <Avatar
              size="2xl"
              src={profile.avatarUrl}
              name={profile.displayName}
              bg="orange.400"
            />
            <VStack spacing={2}>
              <Heading size="lg">{profile.displayName}</Heading>
              {rep && (rep.completedBounties > 0 || rep.rating > 0) && (
                <HStack spacing={4}>
                  {rep.completedBounties > 0 && (
                    <Text fontSize="sm" color="gray.500">
                      {rep.completedBounties} tasks completed
                    </Text>
                  )}
                  {rep.rating > 0 && (
                    <Text fontSize="sm" color="gray.500">
                      {rep.rating.toFixed(1)} rating
                    </Text>
                  )}
                </HStack>
              )}
            </VStack>
            <NewMessageButton targetUid={profile.uid || researcherId} />
          </VStack>
        </Box>

        {/* About */}
        <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
          <VStack align="stretch" spacing={4}>
            <Heading size="sm">About</Heading>
            <Divider />
            <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
              {profile.bio}
            </Text>
          </VStack>
        </Box>

        {/* Services */}
        {profile.services?.length > 0 && (
          <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
            <VStack align="stretch" spacing={4}>
              <Heading size="sm">Services Offered</Heading>
              <Divider />
              <Wrap spacing={2}>
                {profile.services.map((key) => {
                  const typeInfo = RESEARCH_TYPES[key] || { label: key, color: 'gray' };
                  return (
                    <WrapItem key={key}>
                      <Badge
                        colorScheme={typeInfo.color}
                        fontSize="sm"
                        px={3}
                        py={1}
                        rounded="md"
                      >
                        {typeInfo.label}
                      </Badge>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </VStack>
          </Box>
        )}

        {/* Contact */}
        {profile.websiteUrl && (
          <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
            <VStack align="stretch" spacing={4}>
              <Heading size="sm">Contact</Heading>
              <Divider />
              <VStack align="stretch" spacing={3}>
                {profile.websiteUrl && (
                  <HStack spacing={3}>
                    <Icon as={FiGlobe} color="gray.400" />
                    <ChakraLink href={profile.websiteUrl} isExternal fontSize="sm" color="orange.500">
                      {profile.websiteUrl}
                    </ChakraLink>
                  </HStack>
                )}
              </VStack>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
